/**
 * Preventive-care-gap decision support (`BACKLOG.md` C12) — this product's first Clinical Decision
 * Support Intervention (DSI). `emr-po BACKLOG.md` C6 settles the product posture this is built to:
 * AI/CDS features are in scope, **informational/non-directive only** — the clinician evaluates every
 * recommendation, the software never issues an automated diagnosis or order. See
 * `server/src/clinical/preventiveCareRoutes.ts`'s own header for how that posture is enforced
 * structurally (no code path here or in the route layer ever writes to `medications`, `problems`, or
 * `clinical_notes`).
 *
 * **What this is, precisely, under ONC's HTI-1 final rule.** §170.315(b)(11) (Decision Support
 * Interventions) distinguishes an **evidence-based DSI** (logic translated from a cited guideline or
 * research finding) from a **Predictive DSI** (an intervention whose output is produced by a model
 * trained on data, e.g. a statistical or ML model). This module is a pure, deterministic rule engine
 * over structured chart data — age thresholds and lookback windows translated from the CDC/ACIP
 * immunization schedule (https://www.cdc.gov/vaccines/hcp/imz-schedules/index.html). **No AI or
 * predictive-model component is involved anywhere in this feature.** That is a deliberate scope
 * choice, not an oversight: a genuine Predictive DSI needs nine additional documentation categories
 * (training-data provenance, external validation, quantitative fairness/performance metrics, ongoing
 * monitoring) plus a documented Intervention Risk Management program — building that honestly is real
 * future work, not something a first, narrow DSI instance should claim. This module is therefore an
 * evidence-based DSI, and `DsiSourceAttributes` below carries exactly the source-attribute set HTI-1
 * requires for that category (§170.315(b)(11)(iv)(A)): a bibliographic citation, the intervention's
 * developer, its funding source, release/revision dates, and — critically, the part easy to miss
 * because it must be documented even when the answer is "no" — an explicit statement of whether the
 * intervention uses each of race/ethnicity, language, sexual orientation/gender identity, sex, date of
 * birth, social determinants of health, and health-status-assessment data. Every recommendation this
 * module produces carries this object structurally (`PreventiveCareRecommendation.source`), not merely
 * as display copy a UI could omit — the DSI-transparency requirement is that the *system* make this
 * information available to an identified user, not that a screen happens to render some of it once.
 *
 * **Deliberately simplified relative to the full ACIP schedule — a small, explainable v1, matching
 * `patientMatching.ts`'s own precedent for what "first, narrow, real" means here.** Three rules,
 * chosen to exercise three different rule shapes (annual, decade booster, one-time-after-age) rather
 * than to be a clinically exhaustive immunization-gap engine: an exhaustive one needs coded vaccine
 * terminology (`db/schema/clinical.ts`'s own comment on `immunizations.vaccine` says free-text coding
 * is real future work, not something to fake today), pregnancy/immunocompromise/occupational-exposure
 * branches, and catch-up schedules this v1 does not attempt. `vaccineMatchTerms` matching is a
 * case-insensitive substring test against free-text `immunizations.vaccine`, the same "no coded
 * terminology yet" limitation `problems.icd10Code` and `allergies.reaction`/`severity` already carry —
 * a vaccine recorded under a name this module's terms do not match (a brand name, a typo) is silently
 * not credited, which undercounts a genuinely-covered gap rather than overcounting one; the safer
 * direction for a non-directive suggestion the clinician reviews before acting on.
 */

export const PREVENTIVE_CARE_ACK_ACTIONS = ['acknowledged', 'ordered', 'dismissed'] as const;
export type PreventiveCareAckAction = (typeof PREVENTIVE_CARE_ACK_ACTIONS)[number];

/**
 * HTI-1 §170.315(b)(11)(iv)(A) source attributes for an evidence-based DSI. Every field here is
 * required precisely because the criterion requires *documenting the absence* of a data element's use
 * as much as its presence — `usesRaceOrEthnicity: false` is itself the disclosure, not an omitted
 * field standing in for "not applicable."
 */
export interface DsiSourceAttributes {
  /** Always `'rule_based'` in this build — see this module's header. Reserved so a future genuine
   * Predictive DSI can be told apart structurally rather than by convention. */
  readonly interventionType: 'rule_based';
  readonly bibliographicCitation: string;
  readonly interventionDeveloper: string;
  readonly fundingSource: string;
  /** ISO date the rule set producing this recommendation was first released. */
  readonly releaseDate: string;
  /** ISO date this specific rule was last revised. Equal to `releaseDate` until a revision happens. */
  readonly revisionDate: string;
  /** The structured fields this rule actually reads, named by table.column — the audit trail for
   * "what data drove this," independent of the plain-language `summary` text. */
  readonly dataElementsUsed: readonly string[];
  readonly usesRaceOrEthnicity: boolean;
  readonly usesLanguage: boolean;
  readonly usesSexualOrientationOrGenderIdentity: boolean;
  readonly usesSex: boolean;
  readonly usesDateOfBirth: boolean;
  readonly usesSocialDeterminantsOfHealth: boolean;
  readonly usesHealthStatusAssessments: boolean;
}

/** Source attributes shared by every rule in this v1 set — none of them touch anything beyond date of
 * birth and immunization history, so this object is spread into each rule's own `source` below rather
 * than restated per rule. */
const COMMON_SOURCE: Omit<DsiSourceAttributes, 'dataElementsUsed'> = {
  interventionType: 'rule_based',
  bibliographicCitation:
    'CDC/Advisory Committee on Immunization Practices (ACIP) Immunization Schedules — ' +
    'https://www.cdc.gov/vaccines/hcp/imz-schedules/index.html',
  interventionDeveloper: 'Southwest EMR clinical content team',
  fundingSource: 'Internal — no external funding',
  releaseDate: '2026-08-24',
  revisionDate: '2026-08-24',
  usesRaceOrEthnicity: false,
  usesLanguage: false,
  usesSexualOrientationOrGenderIdentity: false,
  usesSex: false,
  usesDateOfBirth: true,
  usesSocialDeterminantsOfHealth: false,
  usesHealthStatusAssessments: false,
};

interface PreventiveCareRule {
  readonly id: string;
  readonly vaccineLabel: string;
  /** Case-insensitive substring terms; a vaccine matches this rule if its name contains any one of
   * them. See this module's header for why this is a deliberately loose, undercounting match. */
  readonly vaccineMatchTerms: readonly string[];
  readonly minAgeYears: number;
  /** Gap re-opens if no matching immunization was administered within this many years of `asOfDate`.
   * `undefined` means one-time — any matching immunization on record at all satisfies it forever. */
  readonly repeatIntervalYears?: number;
  readonly source: DsiSourceAttributes;
  readonly summary: (ageYears: number) => string;
}

interface DateParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

function parseIsoDate(isoDate: string): DateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (match === null) throw new Error(`invalid calendar date: ${isoDate}`);
  const [, y, m, d] = match;
  const parts = { year: Number(y), month: Number(m), day: Number(d) };
  // Reject an out-of-range month/day (`Date.UTC` would otherwise silently roll it into the next
  // month, e.g. 2026-02-30 becoming 2026-03-02) — a validation gap this module cannot afford, since
  // `patients.date_of_birth` is caller-supplied and a miscalculated age can shift a threshold rule.
  if (Number.isNaN(Date.UTC(parts.year, parts.month - 1, parts.day))) {
    throw new Error(`invalid calendar date: ${isoDate}`);
  }
  const roundTrips =
    new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDate() === parts.day &&
    new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCMonth() === parts.month - 1;
  if (!roundTrips) throw new Error(`invalid calendar date: ${isoDate}`);
  return parts;
}

/**
 * Whole-year age (or interval) between two calendar dates, by exact calendar components — a person
 * turns 65 on their birthday, not before. **Deliberately not a fractional-year approximation** (an
 * earlier version divided elapsed milliseconds by `365.25 * 24h`, which undercounts by a full year
 * right at a threshold boundary for roughly 1 birthday in 4 — caught by this module's own test suite
 * pinning the exact-65th-birthday case, not by inspection: `1961-08-24` to `2026-08-24` is exactly
 * 65 calendar years but only 64.9993 of that approximation's "years," which floors to 64).
 */
function yearsBetween(fromIsoDate: string, toIsoDate: string): number {
  const from = parseIsoDate(fromIsoDate);
  const to = parseIsoDate(toIsoDate);
  let years = to.year - from.year;
  if (to.month < from.month || (to.month === from.month && to.day < from.day)) years -= 1;
  return years;
}

const PREVENTIVE_CARE_RULES: readonly PreventiveCareRule[] = [
  {
    id: 'influenza_annual',
    vaccineLabel: 'Influenza (annual)',
    vaccineMatchTerms: ['influenza', 'flu'],
    minAgeYears: 0, // ACIP recommends from 6 months; this v1 only tracks whole-year ages (see header).
    repeatIntervalYears: 1,
    source: {
      ...COMMON_SOURCE,
      dataElementsUsed: [
        'patients.date_of_birth',
        'immunizations.vaccine',
        'immunizations.administered_date',
      ],
    },
    summary: (ageYears) =>
      `No influenza vaccine on file in the past 12 months for this patient (age ${String(ageYears)}). ` +
      'For clinician review: consider discussing seasonal influenza vaccination per the CDC/ACIP ' +
      'schedule before ordering.',
  },
  {
    id: 'tdap_td_booster',
    vaccineLabel: 'Tdap/Td booster (10-year)',
    vaccineMatchTerms: ['tdap', 'td vaccine', 'tetanus', 'diphtheria'],
    minAgeYears: 11,
    repeatIntervalYears: 10,
    source: {
      ...COMMON_SOURCE,
      dataElementsUsed: [
        'patients.date_of_birth',
        'immunizations.vaccine',
        'immunizations.administered_date',
      ],
    },
    summary: (ageYears) =>
      `No Tdap/Td booster on file in the past 10 years for this patient (age ${String(ageYears)}). ` +
      'For clinician review: consider discussing a tetanus/diphtheria booster per the CDC/ACIP ' +
      'adult schedule before ordering.',
  },
  {
    id: 'pneumococcal_65',
    vaccineLabel: 'Pneumococcal (age 65+)',
    vaccineMatchTerms: ['pneumococcal', 'pcv', 'ppsv', 'prevnar', 'pneumovax'],
    minAgeYears: 65,
    // One-time in this v1 — the real ACIP guidance is a PCV/PPSV series with product-specific
    // sequencing this module does not attempt (see header); a single recorded dose of any matching
    // product satisfies this rule rather than re-opening the gap, which is the undercounting/
    // conservative direction this module commits to throughout.
    source: {
      ...COMMON_SOURCE,
      dataElementsUsed: [
        'patients.date_of_birth',
        'immunizations.vaccine',
        'immunizations.administered_date',
      ],
    },
    summary: (ageYears) =>
      `No pneumococcal vaccine on file for this patient, now age ${String(ageYears)}. ` +
      'For clinician review: consider discussing pneumococcal vaccination per the CDC/ACIP adult ' +
      'schedule before ordering.',
  },
];

/** Exported for tests and for anything that needs to validate a `ruleId` against the closed set
 * (`preventiveCareRequests.ts`'s own schema does this at the wire boundary). */
export const PREVENTIVE_CARE_RULE_IDS: readonly string[] = PREVENTIVE_CARE_RULES.map((r) => r.id);

export interface PreventiveCareRecommendationBasisData {
  readonly dateOfBirth: string;
  /** The most recent matching immunization's `administeredDate`, or `null` if none matched at all. */
  readonly mostRecentAdministeredDate: string | null;
  /** Every immunization's own recorded `vaccine` text that matched this rule's terms, most recent
   * first — the literal chart content the recommendation is grounded in. */
  readonly matchedImmunizationVaccines: readonly string[];
}

export interface PreventiveCareRecommendation {
  readonly ruleId: string;
  readonly vaccineLabel: string;
  readonly patientAgeYears: number;
  /** Plain-language, deliberately non-directive: every rule's `summary` is written as something for
   * the clinician to review, never as an instruction to act. */
  readonly summary: string;
  readonly basisData: PreventiveCareRecommendationBasisData;
  readonly source: DsiSourceAttributes;
}

export interface PreventiveCareImmunizationInput {
  readonly vaccine: string;
  readonly administeredDate: string;
}

export interface EvaluatePreventiveCareInput {
  readonly dateOfBirth: string;
  /** The caller's own "today" — never read from the system clock inside this module, so the function
   * stays pure and a test can pin any date without mocking time. */
  readonly asOfDate: string;
  readonly immunizations: readonly PreventiveCareImmunizationInput[];
}

/**
 * Evaluate every rule against one patient's demographics and immunization history. Returns one
 * recommendation per currently-open gap — a rule the patient's age qualifies for, with no matching
 * immunization recorded inside the rule's own lookback window (or ever, for a one-time rule). A rule
 * the patient does not yet qualify for by age, or that is already satisfied, produces nothing; this
 * function never returns a "satisfied" entry, only open gaps, matching a clinician's actual question
 * ("what, if anything, needs attention here").
 */
export function evaluatePreventiveCareRecommendations(
  input: EvaluatePreventiveCareInput,
): readonly PreventiveCareRecommendation[] {
  const ageYears = yearsBetween(input.dateOfBirth, input.asOfDate);
  const recommendations: PreventiveCareRecommendation[] = [];

  for (const rule of PREVENTIVE_CARE_RULES) {
    if (ageYears < rule.minAgeYears) continue;

    const matches = input.immunizations
      .filter((imm) => {
        const lowered = imm.vaccine.toLowerCase();
        return rule.vaccineMatchTerms.some((term) => lowered.includes(term));
      })
      .toSorted((a, b) => (a.administeredDate < b.administeredDate ? 1 : -1));

    const mostRecent = matches[0] ?? null;

    const satisfied =
      mostRecent !== null &&
      (rule.repeatIntervalYears === undefined ||
        yearsBetween(mostRecent.administeredDate, input.asOfDate) < rule.repeatIntervalYears);

    if (satisfied) continue;

    recommendations.push({
      ruleId: rule.id,
      vaccineLabel: rule.vaccineLabel,
      patientAgeYears: ageYears,
      summary: rule.summary(ageYears),
      basisData: {
        dateOfBirth: input.dateOfBirth,
        mostRecentAdministeredDate: mostRecent?.administeredDate ?? null,
        matchedImmunizationVaccines: matches.map((m) => m.vaccine),
      },
      source: rule.source,
    });
  }

  return recommendations;
}
